import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, 
  TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, 
  DialogActions, IconButton, Paper, Tabs, Tab, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Menu, MenuList, MenuItem as MenuListItem, Divider, Tooltip,
  Badge, LinearProgress, FormControlLabel, Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Assignment as AssignIcon,
  History as HistoryIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Description as DocumentIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import DocumentEditDialog from '../components/DocumentEditDialog';
import TablePagination from '@mui/material/TablePagination';
import DocumentDeleteDialog from '../components/DocumentDeleteDialog';
import DocumentTrackingReportDialog from '../components/DocumentTrackingReportDialog';
import api from '../utils/api';
import { openDocumentPdf } from '../utils/documentFile';
import DocumentAssignmentDialog from '../components/DocumentAssignmentDialog';
import DocumentVersionDialog from '../components/DocumentVersionDialog';

interface DocumentCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  color: string;
  is_active: boolean;
  document_count: number;
}

interface DocumentVersion {
  id: number;
  version: string;
  changelog: any;
  changelog_summary: string;
  created_by: any;
  created_at: string;
  notes: string;
}

interface Document {
  id: number;
  title: string;
  description: string;
  category: DocumentCategory | null;
  file: string;
  file_size: number;
  file_size_display: string;
  version: string;
  is_current_version: boolean;
  visibility: string;
  requires_acknowledgment: boolean;
  revision_date: string;
  effective_date: string | null;
  expiration_date: string | null;
  is_active: boolean;
  created_by: any;
  created_at: string;
  acknowledgment_summary: any;
  version_history?: DocumentVersion[];
  requires_revision_for_edits?: boolean;
}

interface DocumentFormData {
  title: string;
  description: string;
  category_id: number;
  visibility: string;
  allowed_departments: string[];
  allowed_roles: string[];
  allowed_locations: string[];
  requires_acknowledgment: boolean;
  effective_date: string;
  expiration_date: string;
}

const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [docCount, setDocCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // UI State
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('');
  const [showHistoricalVersions, setShowHistoricalVersions] = useState(false);
  
  // Dialog States
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openAssignmentDialog, setOpenAssignmentDialog] = useState(false);
  const [openVersionDialog, setOpenVersionDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  
  // Edit dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState<Document | null>(null);
  
  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  
  // Tracking report dialog state
  const [openTrackingReportDialog, setOpenTrackingReportDialog] = useState(false);
  const [documentForTrackingReport, setDocumentForTrackingReport] = useState<Document | null>(null);
  
  // More options menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocumentForMenu, setSelectedDocumentForMenu] = useState<Document | null>(null);
  
  // Form Data
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentForm, setDocumentForm] = useState<DocumentFormData>({
    title: '',
    description: '',
    category_id: 0, // Will be set to first category when categories load
    visibility: 'assigned',
    allowed_departments: [],
    allowed_roles: [],
    allowed_locations: [],
    requires_acknowledgment: true,
    effective_date: '',
    expiration_date: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: 'description',
    color: '#1976d2'
  });

  // Fetch Data
  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, [page, rowsPerPage]);

  // Set default category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && (documentForm.category_id === 0 || !documentForm.category_id)) {
      setDocumentForm(prev => ({ ...prev, category_id: categories[0].id }));
    }
  }, [categories]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('page_size', String(rowsPerPage));
      const response = await api.get(`/documents/?${params.toString()}`);
      const results = response.data.results || response.data;
      setDocuments(results);
      setDocCount(response.data.count ?? results.length ?? 0);
    } catch (error) {
      setError('Failed to fetch documents');
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/document-categories/');
      setCategories(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Document Upload
  const handleDocumentUpload = async () => {
    try {
      if (!selectedFile || !documentForm.title || !documentForm.category_id) {
        setError('Please provide a title, select a file, and choose a category');
        return;
      }

      if (!(selectedFile instanceof File)) {
        setError('A valid file must be selected for upload.');
        console.error('Upload aborted: selectedFile is not a valid File object.', selectedFile);
        return;
      }
      
      const formData = new FormData();
      formData.append('title', documentForm.title);
      formData.append('description', documentForm.description);
      formData.append('file', selectedFile);
      formData.append('visibility', documentForm.visibility);
      formData.append('requires_acknowledgment', documentForm.requires_acknowledgment.toString());
      formData.append('category_id', documentForm.category_id.toString());



      // Use proper DRF endpoint with custom create method
      // NOTE: Don't set Content-Type header - let browser set it automatically for FormData
      await api.post('/documents/', formData);

      setSuccess('Document uploaded successfully');
      setOpenUploadDialog(false);
      resetDocumentForm();
      fetchDocuments();
    } catch (error: any) {
      console.error('--- Full Error Object ---', error);
      if (error.response) {
        console.error('--- Server Response Data ---', error.response.data);
        console.error('--- Stringified Response Data ---', JSON.stringify(error.response.data, null, 2));

        const errorData = error.response.data;
        let errorMessage = `Upload Failed: ${JSON.stringify(errorData)}`;
        setError(errorMessage);

      } else if (error.request) {
        console.error('--- No Response Received ---', error.request);
        setError('No response from server. Please check your network connection.');
      } else {
        console.error('--- Request Setup Error ---', error.message);
        setError('An unexpected error occurred while setting up the request.');
      }
    }
  };

  // Category Management
  const handleCreateCategory = async () => {
    try {
      if (!categoryForm.name) {
        setError('Please provide a category name');
        return;
      }

      await api.post('/document-categories/', categoryForm);
      setSuccess('Category created successfully');
      setOpenCategoryDialog(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create category');
      console.error('Error creating category:', error);
    }
  };

  // Helper Functions
  const resetDocumentForm = () => {
    setDocumentForm({
      title: '',
      description: '',
      category_id: categories.length > 0 ? categories[0].id : 0,
      visibility: 'assigned',
      allowed_departments: [],
      allowed_roles: [],
      allowed_locations: [],
      requires_acknowledgment: true,
      effective_date: '',
      expiration_date: ''
    });
    setSelectedFile(null);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      icon: 'description',
      color: '#1976d2'
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError('Only PDF files are allowed');
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        setError('File size cannot exceed 25MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  // Filter Documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || doc.category?.id === selectedCategory;
    const matchesVisibility = !visibilityFilter || doc.visibility === visibilityFilter;
    const matchesVersion = showHistoricalVersions || doc.is_current_version;
    
    return matchesSearch && matchesCategory && matchesVisibility && matchesVersion;
  });

  const getVisibilityColor = (visibility: string) => {
    switch (visibility) {
      case 'public': return 'success';
      case 'department': return 'info';
      case 'role': return 'warning';
      case 'location': return 'secondary';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // More options menu handlers
  const handleMoreOptionsClick = (event: React.MouseEvent<HTMLElement>, document: Document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocumentForMenu(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocumentForMenu(null);
  };

  const handleEditDocument = (document: Document) => {
    setDocumentToEdit(document);
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleOpenRevisionFromEdit = () => {
    if (documentToEdit) {
      setSelectedDocument(documentToEdit);
      setOpenEditDialog(false);
      setOpenVersionDialog(true);
    }
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setDocumentToEdit(null);
    // Refresh documents after dialog closes
    fetchDocuments();
  };

  const handleDeleteDocument = (document: Document) => {
    setDocumentToDelete(document);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDocumentDeleted = () => {
    fetchDocuments();
    setSuccess('Document deactivated successfully');
  };

  const handleViewDocument = async (document: Document) => {
    try {
      await openDocumentPdf(document.id);
    } catch (error) {
      setError('Unable to open document. The file may be missing and need to be re-uploaded.');
      console.error('Error opening document:', error);
    }
  };

  const handleViewTrackingReport = (document: Document) => {
    setDocumentForTrackingReport(document);
    setOpenTrackingReportDialog(true);
    handleMenuClose();
  };

  const handleTrackingReportDialogClose = () => {
    setOpenTrackingReportDialog(false);
    setDocumentForTrackingReport(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Document Management
        </Typography>
        <Box display="flex" gap={2} sx={{ ml: 3 }}>
          <Button
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={() => setOpenCategoryDialog(true)}
          >
            Manage Categories
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setOpenUploadDialog(true)}
          >
            Upload Document
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="All Documents" />
          <Tab label="Categories" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search documents"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category"
                    onChange={(e) => setSelectedCategory(e.target.value as number)}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={visibilityFilter}
                    label="Visibility"
                    onChange={(e) => setVisibilityFilter(e.target.value)}
                  >
                    <MenuItem value="">All Visibility</MenuItem>
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="department">Department</MenuItem>
                    <MenuItem value="role">Role</MenuItem>
                    <MenuItem value="location">Location</MenuItem>
                    <MenuItem value="assigned">Assignment Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showHistoricalVersions}
                      onChange={(e) => setShowHistoricalVersions(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show historical"
                  sx={{ whiteSpace: 'nowrap' }}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDocuments.length} docs
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Documents Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell>Acknowledgments</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <DocumentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="subtitle2">{document.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {document.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {document.file_size_display}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {document.category && (
                        <Chip
                          label={document.category.name}
                          size="small"
                          sx={{ backgroundColor: document.category.color, color: 'white' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{document.version}</Typography>
                      {document.is_current_version && (
                        <Chip label="Current" size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={document.visibility}
                        size="small"
                        color={getVisibilityColor(document.visibility) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {document.requires_acknowledgment ? (
                        <Box>
                          <Typography variant="body2">
                            {document.acknowledgment_summary?.acknowledged || 0} / {document.acknowledgment_summary?.total || 0}
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={document.acknowledgment_summary?.percentage || 0}
                            sx={{ width: 80 }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Not Required</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(document.created_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {document.created_by ? `${document.created_by.first_name} ${document.created_by.last_name}` : 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewDocument(document)}
                        title="View Document"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setSelectedDocument(document);
                          setOpenAssignmentDialog(true);
                        }}
                        title="Assign Document"
                      >
                        <AssignmentIcon />
                      </IconButton>
                      {document.is_current_version ? (
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setSelectedDocument(document);
                            setOpenVersionDialog(true);
                          }}
                          title="Version History"
                        >
                          <HistoryIcon />
                        </IconButton>
                      ) : (
                        <IconButton 
                          size="small"
                          disabled
                          title="Version History (Only available for current version)"
                        >
                          <HistoryIcon />
                        </IconButton>
                      )}
                      <IconButton 
                        size="small" 
                        title="More Options"
                        onClick={(e) => handleMoreOptionsClick(e, document)}
                      >
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={docCount}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}

      {/* Categories Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: category.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}
                    >
                      <DocumentIcon sx={{ color: 'white' }} />
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6">{category.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {category.document_count} documents
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {category.description}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip 
                      label={category.is_active ? 'Active' : 'Inactive'} 
                      size="small"
                      color={category.is_active ? 'success' : 'default'}
                    />
                    <IconButton size="small">
                      <EditIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Document Dialog */}
      <Dialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload New Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Title"
                value={documentForm.title}
                onChange={(e) => setDocumentForm(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={documentForm.description}
                onChange={(e) => setDocumentForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={documentForm.category_id || ''}
                  label="Category"
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, category_id: e.target.value as number }))}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={documentForm.visibility}
                  label="Visibility"
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, visibility: e.target.value }))}
                >
                  <MenuItem value="assigned">Assignment Only</MenuItem>
                  <MenuItem value="public">Public - All Users</MenuItem>
                  <MenuItem value="department">Department Restricted</MenuItem>
                  <MenuItem value="role">Role Restricted</MenuItem>
                  <MenuItem value="location">Location Restricted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' }
                }}
                onClick={() => {
                  const fileInput = (window.document as any).getElementById('file-input');
                  if (fileInput) fileInput.click();
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Click to upload PDF file'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PDF files only, maximum 25MB
                </Typography>
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)}>Cancel</Button>
          <Button onClick={handleDocumentUpload} variant="contained">Upload</Button>
        </DialogActions>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Icon"
                value={categoryForm.icon}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, icon: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={categoryForm.color}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Document Assignment Dialog */}
      <DocumentAssignmentDialog
        open={openAssignmentDialog}
        onClose={() => {
          setOpenAssignmentDialog(false);
          setSelectedDocument(null);
        }}
        documentId={selectedDocument?.id || 0}
        documentTitle={selectedDocument?.title || ''}
        onAssignmentCreated={() => {
          fetchDocuments();
          setSuccess('Document assigned successfully');
        }}
      />

      {/* Document Version Dialog */}
      <DocumentVersionDialog
        open={openVersionDialog}
        onClose={() => {
          setOpenVersionDialog(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onVersionCreated={() => {
          fetchDocuments();
          setSuccess('New document version created successfully');
        }}
      />

      {/* Document Edit Dialog */}
      <DocumentEditDialog
        open={openEditDialog}
        onClose={handleEditDialogClose}
        document={documentToEdit}
        categories={categories}
        onRequiresRevision={handleOpenRevisionFromEdit}
      />

      {/* Document Delete Dialog */}
      <DocumentDeleteDialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        document={documentToDelete}
        onDocumentDeleted={handleDocumentDeleted}
      />

      {/* Document Tracking Report Dialog */}
      <DocumentTrackingReportDialog
        open={openTrackingReportDialog}
        onClose={handleTrackingReportDialogClose}
        document={documentForTrackingReport}
      />

      {/* More Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedDocumentForMenu && handleEditDocument(selectedDocumentForMenu)}>
          <EditIcon sx={{ mr: 1 }} />
          {selectedDocumentForMenu?.requires_revision_for_edits ? 'Create Revision' : 'Edit Document'}
        </MenuItem>
        <MenuItem 
          onClick={() => selectedDocumentForMenu && handleViewTrackingReport(selectedDocumentForMenu)}
        >
          <AssessmentIcon sx={{ mr: 1 }} />
          View Tracking Report
        </MenuItem>
        <MenuItem 
          onClick={() => selectedDocumentForMenu && handleDeleteDocument(selectedDocumentForMenu)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Deactivate Document
        </MenuItem>
      </Menu>

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Documents; 